import * as propz from 'propz';
import { 
    KitBottomPattern,
    KitSkortPattern,
    KitPatternVariant,
    ShortsVariant
 } from 'types/kitLab';
import { 
    PublicEventKitsResponse,
    KitLabBackendKit
} from 'models/kitLab';
import { Participant as ParticipantModel } from 'models/event';
export const OUTLINE_COLOR = '#e5e9f4';
export const OUTLINE_WIDTH = 0.6;

export const outlineStrokeProps = {
  stroke: OUTLINE_COLOR,
  strokeWidth: OUTLINE_WIDTH,
  strokeLinejoin: 'round' as const,
  vectorEffect: 'non-scaling-stroke' as const
};

export const KIT_ICON_SIZE = 32;          
export const KIT_BOTTOM_RATIO = 0.8;     
export const KIT_VERTICAL_OVERLAP = 8; 

export function mapBottomToSkortPattern(pattern: KitBottomPattern): KitSkortPattern {
  switch (pattern) {
    case 'SKORT_VERTICAL_STRIPES':
      return 'verticalStripes';
    case 'SKORT_SIDE':
      return 'sideStripes';
    case 'SKORT_TRIANGLES':
      return 'sideTriangles';
    case 'SKORT_BOTTOM_TRIANGLES':
      return 'sideBottomTriangles';
    case 'SKORT_SEMICIRCLES':
      return 'sideSemiCircles';
    case 'SKORT_PLAIN':
    default:
      return 'plain';
  }
}

function normalizeId(id: any): string | null {
  if (!id) return null;
  try {
    return id.toString();
  } catch {
    return null;
  }
}

export function getKitForSchoolTeam(
  schoolId?: string | null,
  teamId?: string | null,
  eventKitsData?: PublicEventKitsResponse | null
): KitLabBackendKit | null {
  if (!eventKitsData || !schoolId) return null;

  const { mappings, kits } = eventKitsData;
  if (!Array.isArray(mappings) || !Array.isArray(kits) || !kits.length) {
    return null;
  }

  const schoolIdStr = normalizeId(schoolId);
  if (!schoolIdStr) return null;

  const mapping =
    mappings.find(m => {
      const sid = normalizeId(propz.get(m, ['schoolId'], null));
      return sid === schoolIdStr;
    }) || null;

  if (!mapping) return null;

  const kitMappings: any[] = propz.get(mapping, ['kitLabKits'], []) || [];
  if (!kitMappings.length) return null;

  let mappingEntry: any = null;

  if (teamId) {
    const teamIdStr = normalizeId(teamId);
    mappingEntry =
      kitMappings.find(m => normalizeId(m.teamId) === teamIdStr) || null;
  }

  if (!mappingEntry) {
    mappingEntry =
      kitMappings.find(m => !m.teamId) ||
      kitMappings[0] ||
      null;
  }

  if (!mappingEntry || !mappingEntry.kitId) return null;

  const kitIdStr = normalizeId(mappingEntry.kitId);
  if (!kitIdStr) return null;

  const kit =
    kits.find(k => normalizeId((k as any)._id) === kitIdStr) || null;

  return kit;
}

export function getKitForParticipant(
  participant: ParticipantModel,
  eventKitsData?: PublicEventKitsResponse | null
): KitLabBackendKit | null {
  const schoolId = propz.get(participant, ['school', 'id'], null);
  const teamId = propz.get(participant, ['team', 'id'], null);
  return getKitForSchoolTeam(schoolId, teamId, eventKitsData);
}

export function isSkortPatternId(pattern?: KitBottomPattern): boolean {
  if (!pattern) return false;
  return (
    pattern === 'SKORT_PLAIN' ||
    pattern === 'SKORT_VERTICAL_STRIPES' ||
    pattern === 'SKORT_SIDE' ||
    pattern === 'SKORT_TRIANGLES' ||
    pattern === 'SKORT_BOTTOM_TRIANGLES' ||
    pattern === 'SKORT_SEMICIRCLES'
  );
}

export function getKitPreviewLayout(kit: KitLabBackendKit) {
  let primaryColor = '#1f78ff';
  let secondaryColor = '#ffd24d';
  let shortsPrimaryColor = primaryColor;
  let shortsSecondaryColor = secondaryColor;
  let shirtPattern: KitPatternVariant = 'plain';
  let bottomPattern: KitBottomPattern = 'SHORTS_PLAIN';

  if (kit.layoutPreset) {
    try {
      const parsed = JSON.parse(kit.layoutPreset as any) as any;
      if (parsed) {
        if (parsed.primaryColor) primaryColor = parsed.primaryColor;
        if (parsed.secondaryColor) secondaryColor = parsed.secondaryColor;
        if (parsed.shortsPrimaryColor) shortsPrimaryColor = parsed.shortsPrimaryColor;
        if (parsed.shortsSecondaryColor) shortsSecondaryColor = parsed.shortsSecondaryColor;
        if (parsed.shirtPattern) shirtPattern = parsed.shirtPattern as KitPatternVariant;
        if (parsed.bottomPattern) bottomPattern = parsed.bottomPattern as KitBottomPattern;
      }
    } catch {
    }
  }

  if (!kit.layoutPreset && Array.isArray(kit.segments)) {
    const primarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHIRT_PRIMARY');
    const secondarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHIRT_SECONDARY');
    const shortsPrimarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHORTS_PRIMARY');
    const shortsSecondarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHORTS_SECONDARY');

    if (primarySeg?.color) primaryColor = primarySeg.color;
    if (secondarySeg?.color) secondaryColor = secondarySeg.color;
    if (shortsPrimarySeg?.color) shortsPrimaryColor = shortsPrimarySeg.color;
    if (shortsSecondarySeg?.color) shortsSecondaryColor = shortsSecondarySeg.color;
  }

  const bottomIsSkort = isSkortPatternId(bottomPattern);
  const skortPattern: KitSkortPattern = bottomIsSkort ? mapBottomToSkortPattern(bottomPattern) : 'plain';

  let shortsVariant: ShortsVariant = 'plain';
  if (!bottomIsSkort) {
    switch (bottomPattern) {
      case 'SHORTS_SIDE':
        shortsVariant = 'sideStripes';
        break;
      case 'SHORTS_TRIANGLES':
        shortsVariant = 'sideTriangles';
        break;
      case 'SHORTS_BIG_TRIANGLES':
        shortsVariant = 'sideBigTriangles';
        break;
      case 'SHORTS_SEMICIRCLES':
        shortsVariant = 'sideSemiCircles';
        break;
      default:
        shortsVariant = 'plain';
    }
  }

  return {
    shirtPattern,
    primaryColor,
    secondaryColor,
    bottomIsSkort,
    shortsVariant,
    skortPattern,
    shortsPrimaryColor,
    shortsSecondaryColor
  };
}